import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import LiveDot from '../../../atoms/LiveDot';
import BandAvatar from '../../../atoms/BandAvatar';
import HeroCoverEditButton from '../../../atoms/HeroCoverEditButton';
import EditableText from '../../../molecules/EditableText';
import StyleTagsEditor from '../../../molecules/StyleTagsEditor';
import EditorialHeroStatsRail from '../../../molecules/EditorialHeroStatsRail';
import { EyeIcon, PencilIcon, ShareIcon } from '../../../atoms/icons/EditorialProfileIcons';
import PostVisibilityControl from '../../../molecules/PostVisibilityControl';
import { BAND_VISIBILITY_OPTIONS } from '../../../molecules/PostVisibilityControl/options';
import classNames from '../../../../utils/classNames';
import { normalizeStyleTagList } from '../../../../utils/styleTags';

const ArrowRightIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M5 12h14M13 5l7 7-7 7" />
  </svg>
);

const HeartIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.29 1.51 4.04 3 5.5l7 7Z" />
  </svg>
);

const LeaveIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M10 6H6a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h4" />
    <path d="M14 8l4 4-4 4" />
    <path d="M18 12H9" />
  </svg>
);

function renderSeparator(index, length) {
  if (index >= length - 1) return null;
  return <span className="band-hero__sep" aria-hidden="true">/</span>;
}

export default function BandHero({
  id,
  bandName,
  avatarUrl,
  avatarSeed,
  monogram,
  tagline,
  status,
  facts = [],
  stats = [],
  onFollow,
  onLeaveBand,
  onTickets,
  onShare,
  onPublicView,
  visibility,
  onVisibilityChange,
  visibilityDisabled = false,
  followLabel,
  leaveBandLabel,
  ticketsLabel,
  shareLabel,
  publicLabel,
  followDisabled = false,
  leaveBandDisabled = false,
  tourDatesHref,
  viewTourLabel,
  coverImageUrl,
  onCoverEdit,
  coverEditLabel,
  onAvatarClick,
  onAvatarEdit,
  avatarPreviewLabel,
  avatarEditLabel,
  avatarEditAriaLabel,
  editing = false,
  isOwner = false,
  editableName = '',
  editableDescription = '',
  editableLocation = '',
  editableFoundedYear = '',
  editableTags = [],
  onSaveField,
  onEditLocation,
  onEditToggle,
}) {
  const { t } = useTranslation();

  const statusItems = status ? status.filter(Boolean) : [];
  const renderedAvatar = (
    <BandAvatar
      imageUrl={avatarUrl}
      seed={avatarSeed || bandName}
      name={bandName}
      monogram={monogram}
      showMonogram
    />
  );
  const avatarEditText = avatarEditLabel || t('bandPage.hero.avatarEdit', { defaultValue: 'Edit' });
  const resolvedAvatarEditAriaLabel = avatarEditAriaLabel
    || (typeof avatarEditText === 'string'
      ? avatarEditText
      : t('bandPage.hero.avatarEditAria', { defaultValue: 'Edit band avatar' }));
  const hasAvatarActions = Boolean(onAvatarClick || onAvatarEdit);

  const handleSaveTags = useCallback((value) => {
    const next = normalizeStyleTagList(value, { splitCommas: true });
    return onSaveField?.('styleTags', next);
  }, [onSaveField]);
  const locationPlaceholder = t('bandPage.edit.locationPlaceholder', {
    defaultValue: 'City, region, country',
  });

  // While editing as the owner we always surface editable meta rows so
  // blank backend values can still be set inline.
  const displayedFacts = useMemo(() => {
    if (!editing) return facts;
    const hasHometown = facts.some((f) => f && f.field === 'locationDisplay');
    const hasStyle = facts.some((f) => f && f.field === 'styleTags');
    const hasFounded = facts.some((f) => f && f.field === 'foundedYear');
    const editableFacts = [...facts];
    if (!hasHometown) {
      editableFacts.unshift({
        label: t('bandPage.hero.factHometown', { defaultValue: 'Hometown' }),
        value: '',
        field: 'locationDisplay',
      });
    }
    if (!hasStyle) {
      const foundedIndex = editableFacts.findIndex((f) => f && f.field === 'foundedYear');
      const styleFact = {
        label: t('bandPage.hero.factStyle', { defaultValue: 'Style' }),
        value: '',
        field: 'styleTags',
      };
      if (foundedIndex >= 0) {
        editableFacts.splice(foundedIndex, 0, styleFact);
      } else {
        editableFacts.push(styleFact);
      }
    }
    if (!hasFounded) {
      editableFacts.push({
        label: t('bandPage.hero.factFoundedShort', { defaultValue: 'Founded' }),
        value: editableFoundedYear,
        field: 'foundedYear',
      });
    }
    return editableFacts;
  }, [editableFoundedYear, editing, facts, t]);

  return (
    <section className="band-hero" id={id}>
      <div className="band-hero__bg" />
      <div
        className={classNames('band-hero__photo', coverImageUrl && 'band-hero__photo--image')}
      >
        {coverImageUrl ? (
          <img src={coverImageUrl} alt="" aria-hidden="true" />
        ) : null}
      </div>
      <div className="band-hero__grain" />

      <HeroCoverEditButton
        className="band-hero__cover-edit"
        label={coverEditLabel || t('bandPage.hero.coverEdit', { defaultValue: 'Edit cover' })}
        onClick={onCoverEdit}
      />

      <div className="band-hero__container">
        {statusItems.length > 0 ? (
          <div className="band-hero__top">
            {statusItems.map((item, idx) => (
              <React.Fragment key={idx}>
                <span>
                  {item.live ? <LiveDot color="online" size="sm" /> : null}
                  {item.live ? <span>&nbsp;&nbsp;</span> : null}
                  {item.label}
                </span>
                {renderSeparator(idx, statusItems.length)}
              </React.Fragment>
            ))}
          </div>
        ) : null}

        <div className={classNames('band-hero__avatar', hasAvatarActions && 'band-hero__avatar--interactive')}>
          <div className="band-hero__avatar-shell">
            {onAvatarClick ? (
              <button
                type="button"
                className="band-hero__avatar-trigger"
                onClick={onAvatarClick}
                aria-label={avatarPreviewLabel || t('bandPage.hero.avatarPreview', { defaultValue: 'View band avatar' })}
              >
                {renderedAvatar}
              </button>
            ) : renderedAvatar}
            {onAvatarEdit ? (
              <button
                type="button"
                className="band-hero__avatar-edit"
                onClick={onAvatarEdit}
                aria-label={resolvedAvatarEditAriaLabel}
              >
                <PencilIcon width="14" height="14" />
                <span>{avatarEditText}</span>
              </button>
            ) : null}
          </div>
        </div>

        <h1
          className={classNames('band-hero__title', editing && 'band-hero__title--editable')}
        >
          {editing ? (
            <EditableText
              editing
              value={editableName}
              maxLength={128}
              placeholder={t('bandPage.edit.namePlaceholder', { defaultValue: 'Band name' })}
              ariaLabel={t('bandPage.edit.namePlaceholder', { defaultValue: 'Band name' })}
              onSave={(next) => onSaveField?.('name', next)}
              inputClassName="band-hero__title-input"
            />
          ) : (
            <span>{bandName}</span>
          )}
        </h1>

        {editing ? (
          <p className="band-hero__tagline band-hero__tagline--editable">
            <EditableText
              editing
              multiline
              rows={3}
              value={editableDescription}
              maxLength={512}
              placeholder={t('bandPage.edit.descriptionPlaceholder', { defaultValue: 'Tell people what this band is about' })}
              ariaLabel={t('bandPage.edit.descriptionPlaceholder', { defaultValue: 'Tell people what this band is about' })}
              onSave={(next) => onSaveField?.('description', next)}
            />
          </p>
        ) : (tagline ? (
          <p className="band-hero__tagline">{tagline}</p>
        ) : null)}

        {displayedFacts.length > 0 ? (
          <div className="band-hero__rule-row">
            <div className="band-hero__meta">
              {displayedFacts.map((fact, idx) => {
                const isLocationField = editing && fact.field === 'locationDisplay';
                const isStyleField = editing && fact.field === 'styleTags';
                const isFoundedField = editing && fact.field === 'foundedYear';
                const isEditableField = isLocationField || isStyleField || isFoundedField;
                return (
                  <div
                    className={classNames(
                      'band-hero__meta-item',
                      isEditableField && 'band-hero__meta-item--editable',
                    )}
                    key={idx}
                  >
                    <span className="band-hero__meta-label">{fact.label}</span>
                    {isLocationField ? (
                      <span className="band-hero__meta-value band-hero__meta-value--editable">
                        <button
                          type="button"
                          className={classNames(
                            'band-hero__location-trigger',
                            !editableLocation && 'band-hero__location-trigger--empty',
                          )}
                          onClick={onEditLocation}
                          disabled={!onEditLocation}
                          aria-label={t('bandPage.edit.locationEditAria', { defaultValue: 'Edit band hometown' })}
                        >
                          {editableLocation || locationPlaceholder}
                        </button>
                      </span>
                    ) : isStyleField ? (
                      <span className="band-hero__meta-value band-hero__meta-value--editable">
                        <StyleTagsEditor
                          values={editableTags}
                          onSave={handleSaveTags}
                          placeholder={t('bandPage.edit.tagsPlaceholder', { defaultValue: 'Comma-separated, e.g. indie, post-punk, shoegaze' })}
                          ariaLabel={t('bandPage.edit.tagsAriaLabel', { defaultValue: 'Band styles' })}
                          removeLabel={t('bandPage.edit.removeStyleLabel', { defaultValue: 'Remove {{style}}' })}
                          customOptionLabel={t('bandPage.edit.customStyleOption', { defaultValue: 'Use custom style' })}
                          emptyLabel={t('bandPage.edit.noStyleOptions', { defaultValue: 'No matching styles' })}
                        />
                      </span>
                    ) : isFoundedField ? (
                      <span className="band-hero__meta-value band-hero__meta-value--editable">
                        <EditableText
                          editing
                          value={editableFoundedYear}
                          maxLength={4}
                          inputMode="numeric"
                          pattern="[0-9]*"
                          placeholder={t('bandPage.edit.foundedYearPlaceholder', { defaultValue: 'Founded year' })}
                          ariaLabel={t('bandPage.edit.foundedYearPlaceholder', { defaultValue: 'Founded year' })}
                          onSave={(next) => onSaveField?.('foundedYear', next)}
                        />
                      </span>
                    ) : (
                      <span className="band-hero__meta-value">{fact.value}</span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ) : null}

        <div className="band-hero__actions">
          {isOwner ? (
            <button
              type="button"
              className="band-hero__btn band-hero__btn--accent band-hero__btn--edit"
              onClick={onEditToggle}
              aria-pressed={editing}
            >
              <PencilIcon width="16" height="16" />
              {editing
                ? t('bandPage.edit.exit', { defaultValue: 'Done' })
                : t('bandPage.edit.enter', { defaultValue: 'Edit band' })}
            </button>
          ) : null}
          {isOwner && onVisibilityChange ? (
            <PostVisibilityControl
              value={visibility || 'PUBLIC'}
              onChange={onVisibilityChange}
              disabled={visibilityDisabled}
              size="md"
              className="band-hero__visibility-control"
              options={BAND_VISIBILITY_OPTIONS}
            />
          ) : null}
          {isOwner && onPublicView ? (
            <button type="button" className="band-hero__btn band-hero__btn--ghost" onClick={onPublicView}>
              <EyeIcon width="16" height="16" />
              {publicLabel || t('bandPage.hero.publicView', { defaultValue: 'View as public' })}
            </button>
          ) : null}
          {onFollow ? (
            <button
              type="button"
              className="band-hero__btn band-hero__btn--ghost"
              onClick={onFollow}
              disabled={followDisabled}
            >
              <HeartIcon />
              {followLabel || t('bandPage.hero.follow', { defaultValue: 'Follow' })}
            </button>
          ) : null}
          {onLeaveBand ? (
            <button
              type="button"
              className="band-hero__btn band-hero__btn--danger"
              onClick={onLeaveBand}
              disabled={leaveBandDisabled}
            >
              <LeaveIcon />
              {leaveBandLabel || t('bandPage.hero.leaveBand', { defaultValue: 'Leave band' })}
            </button>
          ) : null}
          {tourDatesHref ? (
            <a className="band-hero__btn band-hero__btn--ghost" href={tourDatesHref}>
              {viewTourLabel || t('bandPage.hero.viewTour')}
            </a>
          ) : null}
          {onTickets ? (
            <button type="button" className="band-hero__btn band-hero__btn--accent" onClick={onTickets}>
              {ticketsLabel || t('bandPage.hero.getTickets')}
              <ArrowRightIcon />
            </button>
          ) : null}
          {onShare ? (
            <button type="button" className="band-hero__btn band-hero__btn--ghost" onClick={onShare}>
              <ShareIcon width="14" height="14" />
              {shareLabel || t('bandPage.hero.share')}
            </button>
          ) : null}
        </div>
      </div>

      <EditorialHeroStatsRail stats={stats} namespace="band-hero" />
    </section>
  );
}

BandHero.propTypes = {
  id: PropTypes.string,
  bandName: PropTypes.string.isRequired,
  avatarUrl: PropTypes.string,
  avatarSeed: PropTypes.string,
  monogram: PropTypes.string,
  tagline: PropTypes.node,
  status: PropTypes.arrayOf(PropTypes.shape({
    live: PropTypes.bool,
    label: PropTypes.node,
  })),
  facts: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.node,
    value: PropTypes.node,
    field: PropTypes.string,
  })),
  stats: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.node,
    label: PropTypes.node,
  })),
  onFollow: PropTypes.func,
  onLeaveBand: PropTypes.func,
  onTickets: PropTypes.func,
  onShare: PropTypes.func,
  onPublicView: PropTypes.func,
  visibility: PropTypes.string,
  onVisibilityChange: PropTypes.func,
  visibilityDisabled: PropTypes.bool,
  followLabel: PropTypes.node,
  leaveBandLabel: PropTypes.node,
  ticketsLabel: PropTypes.node,
  shareLabel: PropTypes.node,
  publicLabel: PropTypes.node,
  followDisabled: PropTypes.bool,
  leaveBandDisabled: PropTypes.bool,
  tourDatesHref: PropTypes.string,
  viewTourLabel: PropTypes.node,
  coverImageUrl: PropTypes.string,
  onCoverEdit: PropTypes.func,
  coverEditLabel: PropTypes.node,
  onAvatarClick: PropTypes.func,
  onAvatarEdit: PropTypes.func,
  avatarPreviewLabel: PropTypes.string,
  avatarEditLabel: PropTypes.node,
  avatarEditAriaLabel: PropTypes.string,
  editing: PropTypes.bool,
  isOwner: PropTypes.bool,
  editableName: PropTypes.string,
  editableDescription: PropTypes.string,
  editableLocation: PropTypes.string,
  editableFoundedYear: PropTypes.string,
  editableTags: PropTypes.arrayOf(PropTypes.string),
  onSaveField: PropTypes.func,
  onEditLocation: PropTypes.func,
  onEditToggle: PropTypes.func,
};
